import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton,
  CircularProgress, Alert, Chip, Divider,
} from '@mui/material';
import CloseIcon     from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { crearUsuarioAdmin } from '../services/authApi';

const ROLES_CONFIG = [
  { rol: 'SuperAdmin',         desc: 'Acceso total',                   color: '#c62828', bg: '#ffebee' },
  { rol: 'AdminConfederacion', desc: 'Admin confederación',            color: '#e65100', bg: '#fff3e0' },
  { rol: 'AdminFederacion',    desc: 'Admin federación',               color: '#e65100', bg: '#fff3e0' },
  { rol: 'AdminCooperativa',   desc: 'Admin cooperativa',              color: '#1565c0', bg: '#e3f2fd' },
  { rol: 'Auditor',            desc: 'Aprueba/rechaza docs y solicitudes', color: '#c62828', bg: '#ffebee' },
  { rol: 'Validador',          desc: 'Valida documentos y solicitudes',color: '#e65100', bg: '#fff3e0' },
  { rol: 'Orientador',         desc: 'Conduce sesiones de orientación',color: '#6a1b9a', bg: '#f3e5f5' },
  { rol: 'Directivo',          desc: 'Directivo de comité',            color: '#1565c0', bg: '#e3f2fd' },
  { rol: 'Afiliado',           desc: 'Afiliado activo',                color: '#2e7d32', bg: '#e8f5e9' },
  { rol: 'Capellan',           desc: 'Capellán misionero',             color: '#6a1b9a', bg: '#f3e5f5' },
  { rol: 'Visitante',          desc: 'Pendiente de aprobación',        color: '#888',    bg: '#f5f5f5' },
];

// Roles internos que se auto-vinculan a FENAGRO al crearse
// (no pasan por flujo de afiliación normal).
const ROLES_STAFF = [
  'SuperAdmin', 'AdminConfederacion', 'AdminFederacion',
  'AdminCooperativa', 'Auditor', 'Validador', 'Orientador',
];

const FORM_INICIAL = {
  email: '', password: '', confirmPassword: '',
  nombre: '', apellido: '', celular: '',
  documento: '', tipoDocumento: 'CC',
  roles: [],
};

export default function CrearUsuarioDialog({ open, onClose, onCreado }) {
  const [form,       setForm]       = useState(FORM_INICIAL);
  const [guardando,  setGuardando]  = useState(false);
  const [error,      setError]      = useState(null);
  const [verPass,    setVerPass]    = useState(false);

  const resetear = () => { setForm(FORM_INICIAL); setError(null); setVerPass(false); };

  const handleClose = () => { resetear(); onClose(); };

  const toggleRol = (rol) => {
    setForm(p => ({
      ...p,
      roles: p.roles.includes(rol) ? p.roles.filter(r => r !== rol) : [...p.roles, rol],
    }));
  };

  const validar = () => {
    if (!form.email.trim())               return 'El email es obligatorio.';
    if (!form.email.includes('@'))        return 'El email no es válido.';
    if (!form.nombre.trim())              return 'El nombre es obligatorio.';
    if (!form.apellido.trim())            return 'El apellido es obligatorio.';
    if (form.password.length < 8)         return 'La contraseña debe tener al menos 8 caracteres.';
    if (form.password !== form.confirmPassword) return 'Las contraseñas no coinciden.';
    if (form.roles.length === 0)          return 'Asigna al menos un rol al usuario.';
    return null;
  };

  const handleGuardar = async () => {
    const err = validar();
    if (err) { setError(err); return; }
    setGuardando(true);
    setError(null);
    try {
      const res = await crearUsuarioAdmin({
        email:         form.email.trim(),
        password:      form.password,
        nombre:        form.nombre.trim(),
        apellido:      form.apellido.trim(),
        celular:       form.celular.trim() || null,
        documento:     form.documento.trim() || null,
        tipoDocumento: form.tipoDocumento,
        roles:         form.roles,
      });
      if (onCreado) onCreado(res);
      handleClose();
    } catch (e) {
      const msg = e?.data?.mensaje || e?.data?.errores?.join(' · ') || 'Error al crear el usuario.';
      setError(msg);
    } finally {
      setGuardando(false);
    }
  };

  const esDominioFenagro = form.email.toLowerCase().endsWith('@fenagro-oficial.org');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

      {/* Header */}
      <DialogTitle sx={{
        background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)',
        color: '#fff', py: 2, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonAddIcon sx={{ fontSize: 20 }} />
          <Box>
            <Typography fontWeight={700} fontSize="0.95rem">Crear nuevo usuario</Typography>
            <Typography fontSize="0.72rem" sx={{ opacity: 0.8 }}>
              Panel de administración — acceso interno
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '24px !important' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Aviso dominio fenagro */}
        {esDominioFenagro && (
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.78rem' }}>
            Este usuario será del dominio <strong>fenagro-oficial.org</strong> (Workspace).
            Su contraseña inicial puede cambiarse después desde el panel.
          </Alert>
        )}

        {/* Aviso auto-vinculación a FENAGRO por rol interno */}
        {form.roles.some(r => ROLES_STAFF.includes(r)) && (
          <Alert severity="success" sx={{ mb: 2, fontSize: '0.78rem' }}>
            Al tener un rol interno ({form.roles.filter(r => ROLES_STAFF.includes(r)).join(', ')}),
            este usuario quedará vinculado automáticamente a la
            <strong> Federación Nacional Agroindustria Ambiental y Producción Limpia </strong>
            (FENAGRO APL) como cooperativa principal, sin pasar por el flujo de afiliación.
          </Alert>
        )}

        {/* ── Datos personales ── */}
        <Typography variant="caption" fontWeight={700} sx={{ color:'#2e7d32', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', mb:1 }}>
          Datos personales
        </Typography>
        <Box sx={{ display:'flex', gap:1.5, mb:1.5 }}>
          <TextField fullWidth size="small" label="Nombre *"
            value={form.nombre} onChange={e => setForm(p => ({...p, nombre: e.target.value}))} />
          <TextField fullWidth size="small" label="Apellido *"
            value={form.apellido} onChange={e => setForm(p => ({...p, apellido: e.target.value}))} />
        </Box>
        <Box sx={{ display:'flex', gap:1.5, mb:1.5 }}>
          <TextField size="small" label="Celular" sx={{ flex: 1 }}
            value={form.celular} onChange={e => setForm(p => ({...p, celular: e.target.value}))} />
          <TextField select size="small" label="Tipo doc." value={form.tipoDocumento}
            onChange={e => setForm(p => ({...p, tipoDocumento: e.target.value}))}
            SelectProps={{ native: true }} sx={{ width: 95, flexShrink: 0 }}>
            {['CC','CE','NIT','PP','TI'].map(t => <option key={t} value={t}>{t}</option>)}
          </TextField>
          <TextField size="small" label="N° Documento" sx={{ flex: 1.5 }}
            value={form.documento} onChange={e => setForm(p => ({...p, documento: e.target.value}))} />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ── Credenciales ── */}
        <Typography variant="caption" fontWeight={700} sx={{ color:'#2e7d32', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', mb:1 }}>
          Credenciales de acceso
        </Typography>
        <TextField fullWidth size="small" label="Email *" type="email" sx={{ mb: 1.5 }}
          value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))}
          helperText="Para usuarios internos usa @fenagro-oficial.org" />
        <Box sx={{ display:'flex', gap:1.5, mb:0.5 }}>
          <TextField fullWidth size="small" label="Contraseña *"
            type={verPass ? 'text' : 'password'}
            value={form.password}
            onChange={e => setForm(p => ({...p, password: e.target.value}))}
            InputProps={{ endAdornment: (
              <IconButton size="small" onClick={() => setVerPass(v => !v)}>
                {verPass ? <VisibilityOffIcon fontSize="small"/> : <VisibilityIcon fontSize="small"/>}
              </IconButton>
            )}} />
          <TextField fullWidth size="small" label="Confirmar contraseña"
            type={verPass ? 'text' : 'password'}
            value={form.confirmPassword}
            onChange={e => setForm(p => ({...p, confirmPassword: e.target.value}))}
            error={!!(form.confirmPassword && form.password !== form.confirmPassword)}
            helperText={form.confirmPassword && form.password !== form.confirmPassword ? 'No coinciden' : ' '} />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ── Roles ── */}
        <Typography variant="caption" fontWeight={700} sx={{ color:'#2e7d32', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', mb:1 }}>
          Roles del usuario *
        </Typography>
        <Box sx={{ display:'flex', flexDirection:'column', gap:0.6, mb:1.5 }}>
          {ROLES_CONFIG.map(({ rol, desc, color, bg }) => {
            const activo = form.roles.includes(rol);
            return (
              <Box key={rol} onClick={() => toggleRol(rol)}
                sx={{
                  display:'flex', alignItems:'center', gap:1.5, p:'8px 12px',
                  borderRadius:2, cursor:'pointer', border:'1.5px solid',
                  borderColor: activo ? color : '#e0e0e0',
                  background:  activo ? bg    : '#fafafa',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: color, background: bg, opacity: 0.9 },
                }}>
                {/* Toggle */}
                <Box sx={{
                  width: 32, height: 18, borderRadius: 9, position: 'relative', flexShrink: 0,
                  background: activo ? color : '#ccc', transition: 'background 0.2s',
                }}>
                  <Box sx={{
                    position:'absolute', top:2, left: activo ? 16 : 2,
                    width:14, height:14, borderRadius:'50%', background:'#fff',
                    transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
                  }}/>
                </Box>
                <Typography fontSize="0.82rem" fontWeight={activo ? 700 : 400}
                  sx={{ color: activo ? color : '#555', minWidth: 140 }}>
                  {rol}
                </Typography>
                <Typography variant="caption" sx={{ color:'#888' }}>{desc}</Typography>
              </Box>
            );
          })}
        </Box>

        {/* Resumen roles seleccionados */}
        {form.roles.length > 0 && (
          <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.5 }}>
            {form.roles.map(r => {
              const cfg = ROLES_CONFIG.find(c => c.rol === r);
              return (
                <Chip key={r} label={r} size="small" onDelete={() => toggleRol(r)}
                  sx={{ fontSize:'0.65rem', height:20, background: cfg?.bg || '#e8f5e9',
                    color: cfg?.color || '#2e7d32', fontWeight:700 }} />
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px:3, py:2, borderTop:'1px solid #f0f0f0' }}>
        <Button onClick={handleClose} sx={{ color:'#888' }}>Cancelar</Button>
        <Button variant="contained" onClick={handleGuardar}
          disabled={guardando}
          startIcon={guardando ? <CircularProgress size={14} color="inherit"/> : <PersonAddIcon/>}
          sx={{ background:'linear-gradient(135deg,#1a4d2e,#2e7d32)', borderRadius:6 }}>
          {guardando ? 'Creando...' : 'Crear usuario'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
